import { Truck } from "lucide-react";
import React from "react";
import { Badge } from "../../../../../components/ui/common/badge";
import RecordEditShell from "../../shared/components/recordList/RecordEditShell";
import RecordStrip from "../../shared/components/recordList/RecordStrip";
import { ChemicalRecord, SelectOptionList, TransportRoute } from "../types/chemicalRecords.types";
import ChemicalRecordForm from "./ChemicalRecordForm";

interface ChemicalRecordRowProps {
  rec: ChemicalRecord;
  idx: number;
  chemFunctionOptions: SelectOptionList;
  chemFunctionLabelMap: Record<string, string>;
  transportOptions: SelectOptionList;
  transportLabelMap: Record<string, string>;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (patch: Partial<ChemicalRecord>) => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  onAddRoute: (chemId: string, route: Pick<TransportRoute, "type" | "distance">) => void;
  onUpdateRoute: (chemId: string, routeId: string, patch: Partial<TransportRoute>) => void;
  onRemoveRoute: (chemId: string, routeId: string) => void;
}

/** One chemical record: a read strip, or (while editing) the inline edit form. */
const ChemicalRecordRow: React.FC<ChemicalRecordRowProps> = ({
  rec,
  idx,
  chemFunctionOptions,
  chemFunctionLabelMap,
  transportOptions,
  transportLabelMap,
  isEditing,
  onEdit,
  onSave,
  onCancelEdit,
  onDelete,
  onAddRoute,
  onUpdateRoute,
  onRemoveRoute
}) => {
  if (isEditing) {
    return (
      <RecordEditShell>
        <ChemicalRecordForm
          rec={rec}
          title={`Chemical ${idx + 1}${rec.name ? ` — ${rec.name}` : ""}`}
          onDelete={onDelete}
          chemFunctionOptions={chemFunctionOptions}
          transportOptions={transportOptions}
          transportLabelMap={transportLabelMap}
          onSave={onSave}
          onCancel={onCancelEdit}
          onAddRoute={onAddRoute}
          onUpdateRoute={onUpdateRoute}
          onRemoveRoute={onRemoveRoute}
        />
      </RecordEditShell>
    );
  }

  return (
    <RecordStrip
      idx={idx}
      title={rec.name}
      badge={
        rec.function && (
          <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
            {chemFunctionLabelMap[rec.function] ?? rec.function}
          </Badge>
        )
      }
      right={
        <>
          {rec.amount || 0} {rec.unit}
        </>
      }
      trailing={
        rec.hasTransportRoute &&
        rec.routes.length > 0 && (
          <Badge variant="secondary" className="tw-text-[10px] tw-h-5 tw-px-1.5">
            <Truck className="tw-h-2.5 tw-w-2.5 tw-mr-1" />
            {rec.routes.length}
          </Badge>
        )
      }
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );
};

export default ChemicalRecordRow;
